import React, { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, Home, RefreshCw } from "lucide-react";
import { Button } from "./ui/button";

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error in React render:", error, errorInfo);
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#0b0d12] flex items-center justify-center p-4">
          <div className="max-w-md w-full rounded-2xl border border-destructive/20 bg-destructive/5 backdrop-blur-sm p-6 sm:p-8 text-center space-y-6 shadow-2xl">
            <div className="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto text-destructive">
              <AlertTriangle className="h-8 w-8" />
            </div>
            
            <div className="space-y-2">
              <h1 className="text-2xl font-bold tracking-tight text-white">Ups, nekaj je šlo narobe!</h1>
              <p className="text-sm text-zinc-400 leading-relaxed">
                Prišlo je do nepričakovane napake pri nalaganju te strani.
              </p>
              {this.state.error && (
                <div className="p-3 bg-black/40 border border-zinc-800 rounded-lg text-left overflow-x-auto text-[11px] font-mono text-destructive select-all">
                  {this.state.error.toString()}
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
              <Button
                variant="outline"
                className="gap-2 border-zinc-800 hover:bg-zinc-800"
                onClick={() => window.location.reload()}
              >
                <RefreshCw className="h-4 w-4 text-white" />
                <span className="text-white">Osveži stran</span>
              </Button>
              <Button
                className="gap-2 bg-primary hover:bg-primary/95 text-primary-foreground font-semibold"
                onClick={() => {
                  this.setState({ hasError: false, error: null });
                  window.location.href = "/";
                }}
              >
                <Home className="h-4 w-4" />
                Domov
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
